/**
 * One workflow's flow, full window — the same diagram a sport's scoring uses.
 *
 * Every workflow is the same kind of flow in the plugin, so Games operations
 * and Results open here exactly as Boxing scoring does. The version shown is
 * the published one (or the newest, when none is published yet), found with
 * GET /scoring/programs?workflow=<code>.
 *
 * The top bar has one short button, "Used in", that lists the apps the workflow
 * opens in and the integrations that send it Scout data. A list, not a row of
 * links, because the bar also holds the centred title and a long row ran into it.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Crumbs, LinkButton, MenuLabel, Popover, Problem, Stack } from "@/components/kit";
import { ProgramEditor } from "@/components/scoring/ProgramEditor";
import styles from "@/components/workflows/workflows.module.css";
import {
  getWorkflow,
  integrationHealth,
  integrationHref,
  listIntegrationBriefs,
  roleDoing,
  type AppLink,
  type IntegrationBrief,
} from "@/lib/api/workflows";
import { getProgram, listPrograms } from "@/lib/scoring/api";
import { rolesFor, variantsFor } from "@/lib/scoring/lens";
import { pickVersion } from "@/lib/scoring/versions";
import { useApi } from "@/spa/useApi";

export function WorkflowFlowPage() {
  const { code = "" } = useParams<{ code: string }>();

  const { data, loading, error } = useApi(
    async () => {
      const row = pickVersion(await listPrograms({ workflow: code }), code);
      if (!row) return { missing: true as const };
      const detail = await getProgram(row.id);
      const [variants, roles] = await Promise.all([
        variantsFor(detail.sportCode || null),
        rolesFor(),
      ]);
      return { missing: false as const, detail, variants, roles };
    },
    [code],
    "failed to load the workflow",
  );

  // Allowed to fail on its own: the diagram still shows without these links.
  const around = useApi(async () => {
    const [detail, integrations] = await Promise.all([
      getWorkflow(code),
      listIntegrationBriefs().catch(() => [] as IntegrationBrief[]),
    ]);
    return {
      apps: detail.apps,
      integrations: integrations.filter((row) => row.workflow === code),
    };
  }, [code]);

  if (error || data?.missing) {
    return (
      <div style={{ padding: "var(--s6)", maxWidth: 760, margin: "0 auto" }}>
        <Stack>
          <Crumbs trail={[{ label: "Workflows", href: "/workflows" }, { label: code }]} />
          <Problem>
            {error
              ? `Could not load the flow for ${code}: ${error}`
              : `${code} has no flow to show yet. Install the plugin's workflows on the Workflows page, then open it again.`}
          </Problem>
          <div style={{ display: "flex", gap: "var(--s2)" }}>
            <LinkButton to="/workflows">All workflows</LinkButton>
          </div>
        </Stack>
      </div>
    );
  }
  if (!data) {
    return loading ? (
      <p role="status" style={{ padding: "var(--s6)", color: "var(--ink-3)" }}>
        Opening the flow…
      </p>
    ) : null;
  }

  return (
    <ProgramEditor
      key={data.detail.id}
      initial={data.detail}
      variants={data.variants}
      roles={data.roles}
      back={{ href: "/workflows", label: "Workflows" }}
      actions={
        around.data && (
          <UsedIn code={code} apps={around.data.apps} integrations={around.data.integrations} />
        )
      }
    />
  );
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

/** Where this workflow is used: its apps, and the integrations that feed it. */
function UsedIn({
  code,
  apps,
  integrations,
}: {
  code: string;
  apps: AppLink[];
  integrations: IntegrationBrief[];
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const label = [
    apps.length === 0 ? "no app yet" : plural(apps.length, "app"),
    integrations.length > 0 ? plural(integrations.length, "integration") : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <span className={styles.flowLinks}>
      <Popover
        open={open}
        onClose={close}
        align="right"
        wide
        trigger={
          <button
            type="button"
            className={styles.flowButton}
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
          >
            Used in: {label}
          </button>
        }
      >
        <MenuLabel>Opens in these apps</MenuLabel>
        {apps.length === 0 ? (
          <p className={styles.flowMenuNote}>No app yet. Nobody can open it until you choose one.</p>
        ) : (
          apps.map((app) => (
            <Link
              key={`${app.appId}/${app.role}`}
              className={styles.flowMenuItem}
              to={`/workflows#app-${app.code}`}
              onClick={close}
            >
              {app.name}
              <span className={styles.small}>
                {roleDoing(app.role)}
                {app.isDefault ? " · default" : ""}
              </span>
            </Link>
          ))
        )}
        {integrations.length > 0 && (
          <>
            <MenuLabel>Scout data comes in through</MenuLabel>
            {integrations.map((row) => (
              <Link
                key={row.code}
                className={styles.flowMenuItem}
                to={integrationHref(row.code)}
                onClick={close}
              >
                {row.name}
                <span className={styles.small}>{integrationHealth(row).word}</span>
              </Link>
            ))}
          </>
        )}
        <Link
          className={styles.flowMenuItem}
          to={`/workflows#workflow-${code}`}
          onClick={close}
        >
          Choose apps
          <span className={styles.small}>on the Workflows page</span>
        </Link>
      </Popover>
    </span>
  );
}
